import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { ShoppingBag, FileText, X } from 'lucide-react';

// Helper
const getSymbol = (curr) => curr === 'INR' ? '₹' : '$';

const UserOrdersPage = () => {
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Fetch List
const { data: orders = [], isLoading } = useQuery({
  queryKey: ['userOrders'],
  queryFn: async () => {
    const res = await axiosClient.get('/account/orders');
    console.log("Orders Fetched:", res);
    return Array.isArray(res) ? res : [];
  }
});

  // Fetch Detail
  const { data: invoice } = useQuery({
    queryKey: ['myInvoice', selectedOrder],
    queryFn: () => axiosClient.get(`/account/orders/${selectedOrder}`),
    enabled: !!selectedOrder
  });

  return (
    <div className="space-y-6 animate-fade-in pb-10">
      <h1 className="text-2xl font-bold text-slate-800">Order History</h1>

      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase">
             <tr>
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4">Invoice #</th>
                <th className="px-6 py-4">Product</th>
                <th className="px-6 py-4">Amount</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Bill</th>
             </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
             {orders.map((order) => (
                <tr key={order.id} className="hover:bg-slate-50 transition-colors">
                   <td className="px-6 py-4 text-sm text-slate-600">
                       {order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}
                   </td>
                   <td className="px-6 py-4 font-mono text-sm font-medium text-slate-700">
                       {order.invoice_number}
                   </td>
                   <td className="px-6 py-4 text-sm text-slate-600">{order.product_name}</td>
                   <td className="px-6 py-4 font-bold text-slate-800">
                       {getSymbol(order.currency)}{order.total_amount}
                   </td>
                   <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                          order.status === 'completed' ? 'bg-green-100 text-green-700' : 
                          order.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                      }`}>
                          {order.status}
                      </span>
                   </td>
                   <td className="px-6 py-4 text-right">
                       <button 
                         onClick={() => setSelectedOrder(order.id)}
                         className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center justify-end w-full"
                       >
                           <FileText className="w-4 h-4 mr-1"/> View
                       </button>
                   </td>
                </tr>
             ))}
          </tbody>
        </table>
        
        {/* Empty State */}
        {orders.length === 0 && !isLoading && (
            <div className="p-10 text-center flex flex-col items-center justify-center text-slate-400">
                <ShoppingBag className="w-10 h-10 mb-2 opacity-20" />
                <p>No orders found for this account.</p>
            </div>
        )}
      </div>

      {/* --- INVOICE MODAL --- */}
      {selectedOrder && invoice && (
        <div className="fixed inset-0 bg-slate-900/80 flex items-center justify-center z-50 backdrop-blur-sm p-4">
           <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95">
              <div className="bg-slate-900 p-6 flex justify-between items-center text-white">
                 <div>
                    <h2 className="text-xl font-bold flex items-center"><FileText className="w-5 h-5 mr-2 text-blue-400"/> Invoice Details</h2>
                    <p className="text-slate-400 text-sm font-mono mt-1">{invoice.invoice_number}</p>
                 </div>
                 <button onClick={() => setSelectedOrder(null)} className="text-white/50 hover:text-white"><X className="w-6 h-6"/></button>
              </div>
              
              <div className="p-8 overflow-y-auto">
                 <div className="flex justify-between mb-8 pb-6 border-b border-slate-100">
                    <div>
                        <p className="text-xs font-bold text-slate-400 uppercase mb-1">Billed To</p>
                        <p className="font-bold text-slate-800">{invoice.user_name}</p>
                        <p className="text-sm text-slate-600">{invoice.user_email}</p>
                    </div>
                    <div className="text-right">
                        <p className="text-xs font-bold text-slate-400 uppercase mb-1">Total Paid</p>
                        <p className="text-2xl font-black text-emerald-600">{getSymbol(invoice.currency)}{invoice.total_amount}</p>
                        <p className="text-xs text-slate-400 mt-1">{new Date(invoice.created_at).toLocaleDateString()}</p>
                    </div>
                 </div>

                 <div className="space-y-3">
                    {invoice.items.map((item, i) => (
                        <div key={i} className="flex justify-between text-sm py-2 border-b border-slate-50 last:border-0">
                            <span className="text-slate-700 font-medium">{item.product_name} <span className="text-slate-400">x{item.quantity}</span></span>
                            <span className="font-mono text-slate-600">{getSymbol(invoice.currency)}{item.price}</span>
                        </div>
                    ))}
                 </div>

                 <div className="mt-6 pt-4 border-t border-slate-100 flex flex-col items-end space-y-1">
                    <div className="flex justify-between w-40 text-sm text-slate-500"><span>Subtotal:</span> <span>{getSymbol(invoice.currency)}{invoice.subtotal}</span></div>
                    <div className="flex justify-between w-40 text-sm text-emerald-600"><span>Discount:</span> <span>-{getSymbol(invoice.currency)}{invoice.discount_amount}</span></div>
                    <div className="flex justify-between w-40 text-sm text-slate-500"><span>Tax ({invoice.tax_percent}%):</span> <span>{getSymbol(invoice.currency)}{invoice.tax_amount}</span></div>
                    <div className="flex justify-between w-40 text-lg font-bold text-slate-800 border-t pt-2 mt-2"><span>Total:</span> <span>{getSymbol(invoice.currency)}{invoice.total_amount}</span></div>
                 </div>
              </div>
           </div>
        </div>
      )}
    </div>
  );
};

export default UserOrdersPage;